import React, { useState } from 'react'
import * as Yup from 'yup'
import { useNavigate } from 'react-router-dom'
import ReactQuill from 'react-quill'
import { useMutation } from '@apollo/client'
import { toast } from 'react-toastify'
import {
  SEND_SET_PASSWORD_MAIL_TO_FRANCHISE_ADMIN,
  UpdateFranchiseAccount
} from '../../../graphql/mutation'
import { GET_ALL_FRANCHISE } from '../../../graphql/query'

type Props = {
  nextStep: any
  prevStep: any
  formData: any
  setFormData: any
  isOffsetLimit: any
}

const OnboardingSchema = Yup.object().shape({
  franchiseAgreementTitle: Yup.string()
    .min(2, 'Minimum 2 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Franchise agreement title is required')
})

const FranchiseAgreement: React.FC<Props> = ({
  nextStep,
  prevStep,
  formData,
  setFormData,
  isOffsetLimit
}) => {
  const navigate = useNavigate()

  const initialAgreementContent = `
  <h1>Employment Agreement</h1>

  <p>This Employment Agreement ("Agreement") is entered into on [Effective Date] by and between:</p>

  <p><strong>Employer:</strong> [Employer Name], a [Legal Structure] organized under the laws of [Jurisdiction], with its principal place of business at [Address].</p>

  <p><strong>Employee:</strong> [Employee Name], residing at [Employee Address].</p>

  <h2>1. Position and Duties</h2>

  <p>The Employer agrees to employ the Employee as [Job Title]. The Employee agrees to diligently and effectively perform the duties and responsibilities assigned by the Employer, including but not limited to:</p>

  <ul>
    <li>[Responsibility 1]</li>
    <li>[Responsibility 2]</li>
  </ul>

  <h2>2. Compensation</h2>

  <p>The Employee will receive a [Salary/Compensation] of [Amount] per [Hour/Week/Month/Year]. Payment will be made [Payment Frequency] via [Payment Method].</p>

  <h2>3. Term and Termination</h2>

  <p>The employment relationship will begin on [Start Date] and continue until terminated by either party. Either party may terminate the employment relationship with or without cause by providing [Notice Period] written notice.</p>

  <h2>4. Confidentiality and Non-Compete</h2>

  <p>During the employment and for a specified period after termination, the Employee agrees not to disclose confidential information and not to engage in competitive activities that may harm the Employer's business.</p>

  <h2>5. Benefits</h2>

  <p>The Employee may be eligible for [Healthcare/Dental/Vacation/Other] benefits as outlined in the Employer's benefit policies.</p>

  <hr />

  <p>This is the initial content of the editor. You can customize and add more details as needed.</p>
  `
  const [editorHtml, setEditorHtml] = useState<string>(initialAgreementContent)

  const handleEditorChange = (content: any) => {
    console.log('Content was updated:', content)
    setEditorHtml(content)
  }

  const handleFileUpload = (event: any) => {
    const file = event.target.files[0]
    if (file && file.type === 'text/plain') {
      const reader = new FileReader()
      reader.onload = (e: any) => {
        setEditorHtml(e.target.result)
      }
      reader.readAsText(file)
    } else {
      alert('Please upload a valid .txt file.')
    }
  }

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'indent',
    'link',
    'image',
    'color',
    'align',
    'script',
    'background',
    'code-block'
  ]

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }, { font: [] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
      ['link', 'image', 'blockquote', 'code-block'],
      [{ color: [] }, { background: [] }, { align: [] }],
      ['clean']
    ]
  }

  const [updateFranchiseAccount] = useMutation(UpdateFranchiseAccount, {
    context: {
      headers: {
        'access-control-allow-methods': 'Franchise'
      }
    }
  })
  const [sendSetPasswordMailtoFranchiseAdmin] = useMutation(
    SEND_SET_PASSWORD_MAIL_TO_FRANCHISE_ADMIN,
    {
      refetchQueries: [
        {
          query: GET_ALL_FRANCHISE,
          variables: {
            input: {
              limit: 10,
              offset: isOffsetLimit
            }
          },
          context: {
            headers: {
              'access-control-allow-methods': 'Franchise'
            }
          }
        }
      ],
      context: {
        headers: {
          'access-control-allow-methods': 'Franchise'
        }
      }
    }
  )

  const handleNavigate = async (event: React.FormEvent) => {
    event.preventDefault()
    try {
      const res = await updateFranchiseAccount({
        variables: {
          input: {
            id: formData.id,
            agreementContent: editorHtml
          }
        },
        context: {
          headers: {
            'access-control-allow-methods': 'Franchise'
          }
        }
      })

      if (res.data.updateFranchiseAccount.status === 200) {
        toast.success('Franchise Agreement Updated Successfully')
        sendSetPasswordMailtoFranchiseAdmin({
          variables: {
            input: { id: res.data.updateFranchiseAccount.data.updatedFranchise.id }
          },
          context: {
            headers: {
              'access-control-allow-methods': 'Franchise'
            }
          }
        })
          .then((sendpasswordresponse: any) => {
            console.log(res.data.updateFranchiseAccount.data.updatedFranchise.id)
            if (sendpasswordresponse.data.sendSetPasswordMailtoFranchiseAdmin.status === 200) {
              toast.success(sendpasswordresponse.data.sendSetPasswordMailtoFranchiseAdmin.message)
              navigate('/franchise/all-lists')
            } else {
              toast.error(sendpasswordresponse.data.sendSetPasswordMailtoFranchiseAdmin.message)
            }
          })
          .catch((err) => {
            console.log(err)
          })
      } else {
        toast.error(res.data.updateFranchiseAccount.message)
      }
    } catch (err) {
      console.error(err)
    }
  }
  console.log(formData)

  return (
    <>
      <div className='collapse show'>
        <form className='form'>
          <div className='card-body border-top p-9'>
            <div className='row mb-6'>
              <h1>Franchise Agreement Details</h1>
            </div>

            <div className='card-body p-9'>
              {/* Editor */}
              <ReactQuill
                theme='snow'
                value={editorHtml}
                onChange={handleEditorChange}
                modules={modules}
                formats={formats}
              />
              {/* Editor */}
            </div>

            <div className='row mb-6 m-15'>
              <div className='col-lg-8 offset-lg-4'>
                <div className='d-flex justify-content-end'>
                  {/* "Previous" button */}
                  <button
                    type='button'
                    className='btn btn-lg btn-secondary mb-5 m-1'
                    onClick={prevStep}
                  >
                    Previous
                  </button>
                  {/* "Next" button */}
                  <button
                    type='submit'
                    className='btn btn-lg btn-primary mb-5 m-1'
                    onClick={handleNavigate}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  )
}

export default FranchiseAgreement
